import { Box, Card, Grid, Stack } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ImageDraggable from 'src/components/image/image-draggable';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import AIImageGeneration from '../ai-image-generation/ai-image-generation.component';
import AIContentComponent from './ai/ai-content.component';
import ConnectedMediaComponent from './connected-media.component';
import { NewPostStyledComponent } from './newPostStyled.component';
import OrgManageConnectedMediaComponent from './org-manage-connected-media.component';
import PostBulkUploadComponent from './post-bulk-upload.component';
import PostDescription from './post-description.component';
import PostFirstComment from './post-first-comment.component';
import PostHashtagGenerator from './post-hashtag-generator.component';
import PostUploadComponent from './post-upload.component';
import { useTranslation } from 'react-i18next';
import PhotoEditor from 'src/components/photo-editor/photo-editor';
import { useResellerLimitDialog } from 'src/components/reseller-limit-context/resellerLimitContext';
import ImageEditor from 'src/components/photo-editor/ImageEditor';
import { useTheme } from '@mui/material/styles';

const PostContentComponent = ({
  configuredSocialMedia,
  selectedSocialMedias,
  handleProfileClick,
  handleNetworks,
  isOrgConnectedProfilesData,
  handleAddAllDescription,
  handleAddAllCommentDescription,
  handleMediaFiles,
  newPostForm,
  hashTags,
  handleAddHashTags,
  onProfileSelectionChange,
  onSocialMediaChange,
  onBrandsChange,
  isShowOfferBanner=false 
}) => {
  const { showToast } = useToaster();

  const navigate = useNavigate();
  const theme = useTheme();
  const { showRightSidebar, handleCloseRightSidebar } = useRightSidebar();
  const { t: translate } = useTranslation();
  const POPUP_WIDTH = '1200px';

  //State Variables
  const [AIHashtagText, setAIHashtagText] = useState('AI Hashtags');
  const { showResellerLimitDialog } = useResellerLimitDialog();
  const [isUploadMediaPopupOpen, setUploadMediaPopup] = useState(false);
  const [isBulkMediaPopupOpen, setBulkMediaPopup] = useState(false);
  const [fileUploadprogress, setFileUploadprogress] = useState();
  const [showAIPopup, setAIPopup] = useState(false);

  const handleUploadMediaPopup = (show = false) => {
    setUploadMediaPopup(show);
  };
  const handleBulkMediaPopup = (show = false) => {
    setBulkMediaPopup(show);
  };
  const handleUploadMedia = (files) => {
    let mFiles = [...newPostForm.media_data];
    mFiles = mFiles.concat(files);
    setUploadMediaPopup(false);
    handleMediaFiles(mFiles);
  };
  const privateFileUploadSuccessCallback = (data, message, uploadFiles = []) => {
    const mFiles = [...data?.media_datas, ...uploadFiles];
    message && showToast(message, 's');
    handleUploadMedia(mFiles);
    handleUploadMediaPopup(false);
  };

  const fileUploadSuccessCallback = (data, message, privateFiles) => {
    if (privateFiles.length > 0) {
      const uploadFilesProps = {
        privateFiles,
        successCallback: (privateData, message) => privateFileUploadSuccessCallback(privateData, message, data),
        failureCallback,
      };
      CommonHelper.UploadPrivateMediaFilesToServer({ ...uploadFilesProps });
    } else {
      message && showToast(message, 's');
      handleUploadMedia(data);
      handleUploadMediaPopup(false);
    }
  };

  const failureCallback = (message) => {
    if ( message && !showResellerLimitDialog(message)) {
      showToast(message, 'e');
    }
  };
  const handleUploadMediaToServer = (files, privateFiles) => {
    if (files.length == 0 && privateFiles.length > 0) {
      const uploadFilesProps = {
        privateFiles,
        successCallback: (data, message) => privateFileUploadSuccessCallback(data, message),
        failureCallback,
      };
      CommonHelper.UploadPrivateMediaFilesToServer({ ...uploadFilesProps });
    } else {
      let uploadFilesProps = {
        files,
        setFileUploadprogress,
        successCallback: (data, message) => {
          fileUploadSuccessCallback(data, message, privateFiles);
        },
        failureCallback,
      };
      CommonHelper.UploadMediaFilesToServer({ ...uploadFilesProps });
    }
  };
  const handleReOrderFiles = (oldIndex, newIndex) => {
    let files = newPostForm.media_data;
    let mediaFiles = CommonHelper.ReArrangeArray(files, oldIndex, newIndex);
    handleMediaFiles(mediaFiles);
  };
  const handleRemoveFiles = (mediaUrl) => {
    let files = [...newPostForm.media_data];
    let index = files.findIndex((a) => a.media_url === mediaUrl);
    if (index >= 0) files.splice(index, 1);
    handleMediaFiles(files);
  };
  const handleAIContent = () => {
    // checkAIEnabledForThisBrand();
    setAIPopup(true);
  };

  const aiEnabledSuccesscallback = (data, message) => {
    if (data) {
      setAIPopup(true);
    } else {
      showToast(
        translate(
          'post_content.openai_api_key_required',
          "Ensure your OpenAI API key is configured in the 'Manage Brand' or 'Manage Organization' section to start using AI-generated content."
        ),
        's'
      );
      navigate('/manage-brand');
    }
  };

  const handleUseAIContent = (content) => {
    handleAddAllDescription(content.trim());
    setAIPopup(false);
  };

  const handleCloseAIPopup = () => {
    setAIPopup(false);
  };

  const hashtagSuccessCallback = (data, message) => {
    if (data.status) {
      if (data && data.content?.length > 0) {
        let hashtags = data.content[0].replaceAll('\n', ' ');
        handleAddHashTags(hashtags);
      }
    } else {
      message && showToast(message, 'e');
    }
    setAIHashtagText(translate('post_content.ai_hashtags', 'AI Hashtags'));
  };

  const handleGenerateHashtags = () => {
    if (newPostForm.description) {
      setAIHashtagText(translate('post_content.generating_hashtags', 'Generating Hashtags'));
      const postProps = {
        url: integrateModuleURL.generateHashtag,
        body: { description: newPostForm.description },
        successCallback: hashtagSuccessCallback,
        failureCallback,
      };
      HttpServices.Post(postProps);
    } else {
      showToast(
        translate(
          'post_content.enter_description_to_generate_hashtags',
          'Please enter description to generate hashtags'
        ),
        'e'
      );
    }
  };

  const handleUseHashtag = (hashtags) => {
    const updatedDescription = `${newPostForm.description} \n ${hashtags}`;
    handleAddAllDescription(updatedDescription);
  };

  const handleImageGeneration = () => {
    let data = {
      content: () => <AIImageGeneration handleGeneratedAIImages={handleGeneratedAIImages} />,
    };
    showRightSidebar(data, POPUP_WIDTH);
  };

  const handleUploadEditedImage = (editedImageData, prevImage = null) => {
    handleCloseRightSidebar();
    let files = [...newPostForm.media_data];
    if (prevImage) {
      let index = files.findIndex((a) => a.media_url === prevImage);
      if (index >= 0) files.splice(index, 1);
    }
    files = files.concat(editedImageData);
    handleMediaFiles(files);
  };

  const handleCloseImageEditor = () => {
    handleCloseRightSidebar();
  };

  const handlePhotoEditor = () => {
    let data = {
      content: () => (
        <ImageEditor
          showToast={showToast}
          translate={translate}
          handleUploadEditedImage={handleUploadEditedImage}
          handleCloseImageEditor={handleCloseImageEditor}
          theme={theme}
          // imageUrl={"https://app.brand2social.com/api/image-server/fetch-media?path=b3f50f68c5fa1060602e707fd4dc670486dc87223940c20c17.jpg"}
        />
      ),
    };
    showRightSidebar(data, POPUP_WIDTH);
  };
  const handleEditFile = (file) => {
    let data = {
      content: () => (
        <ImageEditor
          showToast={showToast}
          translate={translate}
          handleUploadEditedImage={(newFile) => {
            handleUploadEditedImage(newFile, file.media_url);
          }}
          handleCloseImageEditor={handleCloseImageEditor}
          theme={theme}
          imageUrl={file.media_url}
        />
      ),
    };
    showRightSidebar(data, POPUP_WIDTH);
  };
  const handleNewPostEditFile = (file) => {
    handleUploadMediaPopup(false);
    handleEditFile(file);
  };

  const handleGeneratedAIImages = (data) => {
    handleCloseRightSidebar();
    let files = [...newPostForm.media_data];
    files = files.concat(data);
    handleMediaFiles(files);
  };

  return (
    <>
      <Card sx={{ p: 3, height: isShowOfferBanner ? 'calc(100vh - 263px)' : 'calc(100vh - 213px)' , overflow: 'auto' }}>
        <Grid container>
          <Grid item xs={12} alignItems="center">
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <NewPostStyledComponent.LabelStyle>
                {translate('common.profile', 'Profile')}
              </NewPostStyledComponent.LabelStyle>
            </Stack>
          </Grid>
          {!isOrgConnectedProfilesData && (
            <Grid item xs={12}>
              <ConnectedMediaComponent
                configuredSocialMedia={configuredSocialMedia}
                selectedSocialMedias={selectedSocialMedias}
                handleNetworks={handleNetworks}
                handleProfileClick={handleProfileClick}
              />
            </Grid>
          )}
          {isOrgConnectedProfilesData && (
            <Grid item xs={12}>
              <OrgManageConnectedMediaComponent
                orgConnectedProfilesData={configuredSocialMedia.map((profile) => ({
                  ...profile,
                  is_selected: selectedSocialMedias.some((s) => s.id === profile.id),
                }))}
                handleProfileClick={handleProfileClick}
                handleNetworks={handleNetworks}
                onProfileSelectionChange={onProfileSelectionChange}
                onSocialMediaChange={onSocialMediaChange}
                onBrandsChange={onBrandsChange}
              />
            </Grid>
          )}
        </Grid>
        <PostDescription
          handleChangeDescription={handleAddAllDescription}
          valueDescription={newPostForm.description}
          handleUploadMediaPopup={handleUploadMediaPopup}
          handleBulkMediaPopup={handleBulkMediaPopup}
          showInputProps={true}
          handleAIContent={handleAIContent}
          handleGenerateHashtags={handleGenerateHashtags}
          AIHashtagText={AIHashtagText}
          handleImageGeneration={handleImageGeneration}
          handlePhotoEditor={handlePhotoEditor}
        />
        {newPostForm.media_data?.length > 0 && (
          <Box>
            <ImageDraggable
              mediaFiles={newPostForm.media_data}
              handleRemoveFiles={handleRemoveFiles}
              handleReOrderFiles={handleReOrderFiles}
              handleEditFile={handleEditFile}
              isNewpostOrMediaLibray={true}
            />
          </Box>
        )}
        <PostFirstComment
          showInputProps={true}
          handleChangeDescription={handleAddAllCommentDescription}
          valueDescription={newPostForm.comment_description}
        />
        <PostHashtagGenerator hashtags={hashTags} handleUseHashtag={handleUseHashtag} />
      </Card>
      {/* Popup Place */}
      {isUploadMediaPopupOpen && (
        <PostUploadComponent
          handleClose={handleUploadMediaPopup}
          fileUploadprogress={fileUploadprogress}
          handleUpload={handleUploadMediaToServer}
          handleNewPostEditFile={handleNewPostEditFile}
        />
      )}
      {isBulkMediaPopupOpen && (
        <PostBulkUploadComponent handleClose={handleBulkMediaPopup} configuredSocialMedia={configuredSocialMedia} />
      )}
      {showAIPopup && <AIContentComponent handleUseAIContent={handleUseAIContent} handleClose={handleCloseAIPopup} />}
    </>
  );
};
export default PostContentComponent;
